import {
  Button, CircularProgress, Grid,
} from '@material-ui/core';
import { Alert, Skeleton } from '@material-ui/lab';
import type { AxiosError } from 'axios';
import axios from 'axios';
import * as React from 'react';
import useSWR from 'swr';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import { useSnackbar } from 'notistack';
import { useNavigate, useParams } from 'react-router-dom';
import RepairCard from '../metalCollection/RepairCardWithImage';
import DestinationBin from './DestinationBin';
import useEventListener from '../../lib/useEventListener';

const useStyles = makeStyles((theme) => createStyles({
  root: {
    flexGrow: 1,
  },
  completePickupButton: {
    color: theme.palette.common.white,
    backgroundColor: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: theme.palette.primary.light,
    },
  },
}));

interface ICourierPickupScan {
  barcode: string;
  itemCount: number;
  photoUrls: Array<string>;
  destinationBin: string;
}

const CourierPickup: React.FC = () => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const { barcode: barcodeUrlParam } = useParams();
  const initialBarcodeValue = barcodeUrlParam || '';

  const [barcode, setBarcode] = React.useState<string>(initialBarcodeValue);
  const [savingRepair, setSavingRepair] = React.useState(false);

  const repairFetcher = (url: string, barcodeParam: string) => {
    setSavingRepair(true);
    return axios
      .get<ICourierPickupScan>(url, {
      params: {
        barcode: barcodeParam,
      },
    })
      .then((response) => {
        setSavingRepair(false);
        return response.data;
      })
      .catch((e: AxiosError) => {
        setSavingRepair(false);
        enqueueSnackbar(`There was an error when fetching repair ${barcodeParam}: ${JSON.stringify(e?.response?.data)}`, {
          persist: false,
          variant: 'error',
        });
        navigate('/courier');
        throw e; // Rethrow the error to be caught by the useSWR hook
      });
  };

  const { data, isValidating } = useSWR(
    barcode
      ? ['/courier/getcourierrepair', barcode]
      : null,
    (url, barcodeParam) => repairFetcher(url, barcodeParam),
    {
      revalidateOnFocus: false,
      revalidateOnMount: true,
      revalidateOnReconnect: false,
      refreshWhenOffline: false,
      refreshWhenHidden: false,
      refreshInterval: 0,
    },
  );

  let barcodeBuffer = '';

  const handleBarcodeInput = (e: KeyboardEvent) => {
    if (e.key.length === 1 || e.key === 'Enter') {
      if (e.key !== 'Enter') {
        barcodeBuffer = `${barcodeBuffer}${e.key}`;
      } else {
        if (!e.defaultPrevented && barcodeBuffer.length === 10) {
          setBarcode(barcodeBuffer);
        } else {
          enqueueSnackbar(`Could not scan ${barcodeBuffer}.`, { persist: false, variant: 'error' });
        }
        barcodeBuffer = '';
      }
    }
  };

  useEventListener('keydown', handleBarcodeInput);

  return (
    <Grid container spacing={0}>
      <Grid item xs={1} />
      <Grid item xs={8} container spacing={3} alignItems="stretch">
        <Grid item xs={12}>
          <Alert severity="info">{'Continue scanning in repairs 1 at a time, and place in appropriate bin'}</Alert>
        </Grid>
        <Grid item xs={6}>
          {!data
            ? (
              <Skeleton>
                <RepairCard
                  barcode=""
                />
              </Skeleton>
            )
            : (
              <RepairCard
                barcode={data.barcode}
                secondaryText={
        `       ${data.itemCount} item${data.itemCount === 1 ? '' : 's'}`
              }
              />
            )}
        </Grid>
        <Grid item xs={6}>
          {data && !isValidating
            ? (
              <DestinationBin
                destinationBin={data.destinationBin}
              />
            )
            : (
              <Skeleton>
                <DestinationBin destinationBin="" />
              </Skeleton>
            )}
        </Grid>
        <Grid item container direction="row-reverse">
          <Grid item>
            <Button
              id="complete-pickup-button"
              onClick={() => { navigate('/courier'); }}
              fullWidth
              variant="contained"
              color="secondary"
              className={classes.completePickupButton}
              disabled={savingRepair || (!data || isValidating)}
              style={{ minWidth: '9.75rem' }}
            >
              {savingRepair ? <CircularProgress size={25} />
                : 'FINISHED PICKING UP'}
            </Button>
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={3} />
    </Grid>
  );
};

export default CourierPickup;
